import './form-date-field.scss';

import { Controller, useFormContext } from 'react-hook-form';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC } from 'react';
import { Grid, TextField, TextFieldProps } from '@mui/material';

import { ConvergeInputLabel } from './converge-input-label';
import { DateFormatType } from '@converge/data';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FormFieldContainer } from './form-field-container';
import moment from 'moment';
import { formatValue } from '../../domain/entity/utils';

type FormDateFieldProps = {
  name: string;
  containerWidth?: number;
  views?: Array<'day' | 'month' | 'year'>;
  minDate?: Date;
  maxDate?: Date;
  isReadonly?: boolean;
  errorMessage?: string;
  isPrimaryEndorsement?: boolean;
  onDateChange?: (
    value: Date | null,
    keyboardInputValue?: string | undefined
  ) => void;
} & TextFieldProps;

export const dateFormErrorName = 'dateFormat';

export const FormDateField: FC<FormDateFieldProps> = ({
  containerWidth = 6,
  required,
  isReadonly,
  name,
  label,
  views = ['year', 'day'],
  minDate,
  maxDate,
  onDateChange,
  defaultValue,
  errorMessage,
  isPrimaryEndorsement,
  ...otherProps
}) => {
  const { control } = useFormContext();

  const parseValue = (value: any) => {
    if (moment.isMoment(value)) {
      return value; // Already a Moment object
    }
    if (typeof value === 'string') {
      return moment.parseZone(value); // Parse ISO string
    }
    return value ? moment(value) : null; // Parse Date object or null
  };

  const {
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  const hasError = errors[dateFormErrorName] ? true : false;

  const setDateError = () => {
    setError(dateFormErrorName, {
      type: 'DateFormat',
      message: 'Invalid date',
    });
  };
  const clearDateError = () => clearErrors(dateFormErrorName);
  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required,
      }}
      defaultValue={defaultValue}
      render={({
        field: { value, onChange, onBlur, ...otherFields },
        formState: { errors },
      }) => (
        <>
          {!isPrimaryEndorsement ? (
            <FormFieldContainer md={containerWidth}>
              <ConvergeInputLabel label={label} required={required} />
              <DatePicker
                value={parseValue(value)}
                views={views}
                {...otherFields}
                disabled={otherProps.disabled || isReadonly}
                minDate={minDate}
                maxDate={maxDate}
                onChange={(date) => {
                  const formattedDate = formatValue(date);

                  if (formattedDate !== 'Invalid date') {
                    clearDateError();
                    onChange(formattedDate);
                    if (onDateChange && formattedDate) {
                      onDateChange(date, formattedDate);
                    }
                  } else {
                    setDateError();
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    fullWidth
                    size="small"
                    name={name}
                    className="form-date-text-field"
                    {...params}
                    {...otherProps}
                    error={
                      (errorMessage && errorMessage?.length > 0) ||
                      errors[name] ||
                      hasError === true
                        ? true
                        : false
                    }
                    helperText={
                      (errors[name] && errors[name]?.type === 'typeError') ||
                      hasError === true
                        ? `Invalid date`
                        : errors[name]
                        ? (errors[name]?.message as unknown as string)
                        : ''
                    }
                  />
                )}
                inputFormat={DateFormatType.USADateFormat}
                disableMaskedInput
              />
            </FormFieldContainer>
          ) : (
            <FormFieldContainer
              md={containerWidth}
              display="flex"
              justify-content="space-around"
            >
              <Grid item md={4.4} paddingRight={'10%'}>
                <ConvergeInputLabel label={label} required={required} />
              </Grid>
              <Grid item md={4.15}>
                <DatePicker
                  value={value}
                  views={views}
                  {...otherFields}
                  disabled={otherProps.disabled || isReadonly}
                  minDate={minDate}
                  maxDate={maxDate}
                  onChange={(date) => {
                    const formattedDate = formatValue(date);
                    if (formattedDate !== 'Invalid date') {
                      clearDateError();
                      onChange(formattedDate);
                      if (onDateChange && formattedDate) {
                        onDateChange(date, formattedDate);
                      }
                    } else {
                      setDateError();
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      size="small"
                      name={name}
                      sx={{ width: '300px' }}
                      className="form-date-text-field"
                      {...params}
                      {...otherProps}
                      error={
                        (errorMessage && errorMessage?.length > 0) ||
                        errors[name] ||
                        hasError === true
                          ? true
                          : false
                      }
                      helperText={
                        (errors[name] && errors[name]?.type === 'typeError') ||
                        hasError === true
                          ? `Invalid date`
                          : errors[name]
                          ? (errors[name]?.message as unknown as string)
                          : ''
                      }
                    />
                  )}
                  inputFormat={DateFormatType.USADateFormat}
                  disableMaskedInput
                />
              </Grid>
            </FormFieldContainer>
          )}
        </>
      )}
    />
  );
};
